import React, { useState, useEffect, useRef } from 'react';
import { Search, User, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { userContactService } from '@/lib/api/user-contact.service';
import { teamMemberService } from '@/lib/api/teamMember.service';
import type { UserContact } from '@/types/user-contact.types';

interface ContactSuggestion {
  id: number | string;
  full_name: string;
  email: string;
}

interface UserSearchInputProps {
  selectedUser?: UserContact | null;
  onUserSelect: (user: UserContact | null) => void;
  placeholder?: string;
  className?: string;
  filterTeamMembers?: boolean;
  celebrationId?: number;
}

export const UserSearchInput: React.FC<UserSearchInputProps> = ({
  selectedUser,
  onUserSelect,
  placeholder = "Search by name...",
  className = "",
  filterTeamMembers = false,
  celebrationId
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync searchTerm with selectedUser
  useEffect(() => {
    if (selectedUser) {
      setSearchTerm(selectedUser.full_name);
    } else {
      setSearchTerm('');
    }
  }, [selectedUser]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (term: string) => {
    if (!term.trim() && !filterTeamMembers) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      let mapped: ContactSuggestion[] = [];
      
      if (filterTeamMembers && celebrationId) {
        const teamMembers = await teamMemberService.getTeamMembers(celebrationId, false);
        const filtered = teamMembers.filter(tm => 
          tm.user && (
            !term.trim() || 
            tm.user.full_name.toLowerCase().includes(term.toLowerCase()) ||
            (tm.user.email && tm.user.email.toLowerCase().includes(term.toLowerCase()))
          )
        );
        mapped = filtered.map(tm => ({
          id: tm.user.id,
          full_name: tm.user.full_name,
          email: tm.user.email || ''
        }));
      } else {
        const response = await userContactService.searchContacts({ search: term, status: 'active', limit: 10 });
        mapped = response.contacts.map(c => ({
          id: c.id,
          full_name: c.full_name,
          email: c.email || ''
        }));
      }
      
      setSuggestions(mapped);
    } catch (error) {
      console.error('Error searching contacts:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear selection if user is typing
    if (!value.trim()) {
      onUserSelect(null);
    }

    // Debounced search
    const timer = setTimeout(() => {
      performSearch(value);
    }, 300);

    return () => clearTimeout(timer);
  };

  const handleUserSelect = (contact: ContactSuggestion) => {
    setSearchTerm(contact.full_name);
    const fullContact = suggestions.find(s => s.id === contact.id);
    const userContact: UserContact = {
      id: typeof contact.id === 'string' ? parseInt(contact.id as string, 10) : contact.id,
      owner_user_id: (fullContact && (fullContact as any).owner_user_id) ? (fullContact as any).owner_user_id : (typeof contact.id === 'number' ? contact.id : 0),
      user_id: (fullContact && (fullContact as any).user_id) ? (fullContact as any).user_id : (typeof contact.id === 'number' ? contact.id : 0),
      full_name: contact.full_name,
      email: contact.email,
      phone: (fullContact && (fullContact as any).phone) ? (fullContact as any).phone : '',
      relationship: (fullContact && (fullContact as any).relationship) ? (fullContact as any).relationship : '',
      is_primary: (fullContact && (fullContact as any).is_primary) ? (fullContact as any).is_primary : false,
      status: (fullContact && (fullContact as any).status) ? (fullContact as any).status : 'active',
      dietary_preferences: '',
      special_requirements: '',
      metadata: {},
      source: 'user_contact',
      created_at: new Date(),
      updated_at: new Date(),
    };
    onUserSelect(userContact);
    setShowSuggestions(false);
    inputRef.current?.blur();
  };

  const handleClearSelection = () => {
    setSearchTerm('');
    onUserSelect(null);
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    // Load team members when user focuses on the field if we don't have any
    if (suggestions.length === 0) {
      performSearch('');
    }
    setShowSuggestions(true);
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
        <Input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          className="pl-10 pr-10"
        />
        {selectedUser && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearSelection}
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
          </div>
        )}
      </div>

      {/* Suggestions Dropdown - only show when focused and we have results */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((user) => (
            <button
              key={user.id}
              type="button"
              onClick={() => handleUserSelect(user)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="flex-grow min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {user.full_name}
                  </div>
                  <div className="text-sm text-gray-500 truncate">
                    {user.email}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
